import { RULE_ACTION } from '@/utils/tone-policy/severity';
import {
  RETIRED_RULES,
  type NotEnforcedReason,
  type PolicyMode,
  type VerdictSource,
  type Violation,
} from '@/utils/tone-policy/types';

/** Tones this popular are recorded but never actioned by the pipeline; a moderator decides. */
export const POPULAR_TONE_DOWNLOADS = 1_000;

/** A red the pipeline would act on. The eval scores exactly this, so the gate measures what ships. */
const isLiveRed = (violation: Violation): boolean =>
  violation.severity === 'red' && !RETIRED_RULES.includes(violation.rule);

export const isEnforceable = (violation: Violation): boolean =>
  isLiveRed(violation) && violation.confidence !== 'low';

export const hasEnforceableRed = (violations: readonly Violation[]): boolean =>
  violations.some(isEnforceable);

/** Medium means a presumption decided it, so only high strikes, and only for a strike rule. */
const isStrikeable = (violation: Violation): boolean =>
  isEnforceable(violation) &&
  violation.confidence === 'high' &&
  RULE_ACTION[violation.rule] === 'strike';

/** remove_image: every acting finding is about the image, so the image goes and the tone stays up. */
export type EnforcementAction = 'private' | 'remove_image';

const actionOf = (acting: readonly Violation[]): EnforcementAction =>
  acting.every((v) => v.category === 'image-quality') ? 'remove_image' : 'private';

/** The live pipeline and the backfill promote decide the action the same way. */
export const enforcementAction = (violations: readonly Violation[]): EnforcementAction =>
  actionOf(violations.filter(isEnforceable));

/** The findings an enforcement acts on. A moderator's confirmation stands in for the model's confidence. */
export const actingViolations = (
  violations: readonly Violation[],
  moderatorConfirmed: boolean
): Violation[] => violations.filter(moderatorConfirmed ? isLiveRed : isEnforceable);

export interface ModeratorEnforcement {
  strike: boolean;
  action: EnforcementAction;
}

/** A moderator enforcing a held-back verdict: every red acts, and a strike rule strikes. */
export function moderatorEnforcement(
  violations: readonly Violation[]
): ModeratorEnforcement | null {
  const acting = actingViolations(violations, true);
  if (acting.length === 0) return null;
  return {
    strike: acting.some((v) => RULE_ACTION[v.rule] === 'strike'),
    action: actionOf(acting),
  };
}

export type EnforcementDecision =
  | { enforce: true; strike: boolean; action: EnforcementAction }
  | { enforce: false; reason: NotEnforcedReason | null };

export interface EnforcementInput {
  violations: readonly Violation[];
  mode: PolicyMode;
  source: VerdictSource;
  downloads: number;
  isPublic: boolean;
  uploaderVerified: boolean;
}

/** null reason: compliant, nothing to act on. */
export function enforcementDecision(input: EnforcementInput): EnforcementDecision {
  const anyRed = input.violations.some(isLiveRed);
  // Verified creators are never actioned, by the pipeline or a moderator.
  if (input.uploaderVerified && anyRed) return { enforce: false, reason: 'verified' };
  if (!hasEnforceableRed(input.violations)) {
    return { enforce: false, reason: anyRed ? 'low_confidence' : null };
  }
  if (!input.isPublic) return { enforce: false, reason: 'not_public' };
  if (input.downloads >= POPULAR_TONE_DOWNLOADS) return { enforce: false, reason: 'popular' };
  if (input.mode === 'shadow') return { enforce: false, reason: 'shadow' };
  return {
    enforce: true,
    strike: input.source !== 'backfill' && input.violations.some(isStrikeable),
    action: enforcementAction(input.violations),
  };
}

/** Enforcement is opt-in: anything but the exact value "enforce" is shadow. */
export function policyModeFrom(value: string | undefined): PolicyMode {
  return value?.trim().toLowerCase() === 'enforce' ? 'enforce' : 'shadow';
}
